
import React from "react";
import { LayoutGrid, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";

interface ViewModeToggleProps {
  viewMode: "grid" | "table";
  setViewMode: (mode: "grid" | "table") => void;
  borderClass: string;
}

const ViewModeToggle = ({ viewMode, setViewMode, borderClass }: ViewModeToggleProps) => {
  const isMobile = useIsMobile();
  
  // Get current theme from document
  const getCurrentTheme = () => {
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') || 'light';
    }
    return 'light';
  };
  
  const theme = getCurrentTheme();
  
  // Toggle function to switch between grid and table view
  const toggleViewMode = () => {
    setViewMode(viewMode === "grid" ? "table" : "grid");
  };
  
  // Mobile toggle button version
  if (isMobile) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon"
            className={`rounded-md border ${borderClass}`}
            onClick={toggleViewMode}
          >
            {/* Show opposite icon on mobile - list in grid mode, grid in list mode */}
            {viewMode === "grid" ? (
              <List className="h-4 w-4" />
            ) : (
              <LayoutGrid className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>Toggle view mode</p>
        </TooltipContent>
      </Tooltip>
    );
  }
  
  // Desktop version with two buttons
  return (
    <div className={`hidden md:flex items-center rounded-md overflow-hidden border ${borderClass}`}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`rounded-none ${viewMode === "grid" ? "selected-theme" : ""}`}
            onClick={() => setViewMode("grid")}
          >
            <LayoutGrid 
              className={`h-4 w-4 ${
                viewMode === "grid" 
                  ? "" 
                  : (theme === "dark" ? "text-white" : "text-gray-500")
              }`} 
            />
            <span className="sr-only">Grid view</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>Grid view</p>
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`rounded-none ${viewMode === "table" ? "selected-theme" : ""}`}
            onClick={() => setViewMode("table")}
          >
            <List 
              className={`h-4 w-4 ${
                viewMode === "table" 
                  ? "" 
                  : (theme === "dark" ? "text-white" : "text-gray-500")
              }`} 
            />
            <span className="sr-only">List view</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>List view</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

export default ViewModeToggle;
